import { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowLeftRight, RefreshCw, TriangleAlert } from "lucide-react";
import { CampoNumero, CampoSelect, Resultado, Dato, Rejilla } from "../components/campos.jsx";

const API = "https://open.er-api.com/v6/latest/USD";

const MONEDAS = [
  { valor: "MXN", etiqueta: "Peso mexicano (MXN)" },
  { valor: "USD", etiqueta: "Dólar estadounidense (USD)" },
  { valor: "EUR", etiqueta: "Euro (EUR)" },
  { valor: "GBP", etiqueta: "Libra esterlina (GBP)" },
  { valor: "CAD", etiqueta: "Dólar canadiense (CAD)" },
  { valor: "JPY", etiqueta: "Yen japonés (JPY)" },
  { valor: "COP", etiqueta: "Peso colombiano (COP)" },
  { valor: "ARS", etiqueta: "Peso argentino (ARS)" },
  { valor: "CLP", etiqueta: "Peso chileno (CLP)" },
  { valor: "BRL", etiqueta: "Real brasileño (BRL)" },
];

/* Tasas de respaldo por si la API no responde. Son de referencia, no
   del día: la interfaz avisa cuando se están usando estas. */
const RESPALDO = {
  USD: 1, MXN: 19.35, EUR: 0.92, GBP: 0.79, CAD: 1.38,
  JPY: 145.5, COP: 4050, ARS: 1180, CLP: 945, BRL: 5.45,
};

export default function Divisas() {
  const [monto, setMonto] = useState(1000);
  const [desde, setDesde] = useState("USD");
  const [hacia, setHacia] = useState("MXN");
  const [tasas, setTasas] = useState(RESPALDO);
  const [estado, setEstado] = useState("cargando");
  const [actualizado, setActualizado] = useState(null);

  const cargar = useCallback(async (señal) => {
    setEstado("cargando");
    try {
      const res = await fetch(API, { signal: señal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.rates?.MXN) throw new Error("respuesta sin tasas");
      setTasas(data.rates);
      setActualizado(data.time_last_update_utc ?? null);
      setEstado("ok");
    } catch (e) {
      if (e.name === "AbortError") return;
      // El respaldo ya está cargado; sólo cambiamos el aviso.
      setEstado("respaldo");
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    cargar(ctrl.signal);
    return () => ctrl.abort();
  }, [cargar]);

  const resultado = useMemo(() => {
    const a = tasas[desde];
    const b = tasas[hacia];
    if (!a || !b) return 0;
    return (monto / a) * b; // ambas tasas están respecto al USD
  }, [monto, desde, hacia, tasas]);

  const tasaUnitaria = useMemo(() => {
    const a = tasas[desde];
    const b = tasas[hacia];
    return a && b ? b / a : 0;
  }, [desde, hacia, tasas]);

  const fmt = (v, moneda) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda, maximumFractionDigits: 2 }).format(v);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-2xs texto-3">
          {estado === "ok" && `Tasas actualizadas desde open.er-api.com${actualizado ? ` · ${actualizado}` : ""}`}
          {estado === "cargando" && "Sincronizando tipos de cambio…"}
        </span>
        <button
          onClick={() => cargar()}
          disabled={estado === "cargando"}
          className="boton boton-suave disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${estado === "cargando" ? "animate-spin" : ""}`} aria-hidden="true" />
          Actualizar
        </button>
      </div>

      {estado === "respaldo" && (
        <p className="flex items-start gap-2 rounded-xl border border-[color:var(--aviso-borde)] bg-[color:var(--aviso-bg)] p-3 text-2xs text-[color:var(--aviso)]">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          No se pudo contactar al servicio de tipos de cambio. Estás viendo tasas de referencia
          aproximadas, no las del día. No las uses para una operación real.
        </p>
      )}

      <Rejilla>
        <div className="space-y-4">
          <CampoNumero label="Cantidad" valor={monto} onChange={setMonto} min={0} />
          <CampoSelect label="De" valor={desde} onChange={setDesde} opciones={MONEDAS} />

          <button
            onClick={() => {
              setDesde(hacia);
              setHacia(desde);
            }}
            className="boton boton-suave"
          >
            <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
            Invertir
          </button>

          <CampoSelect label="A" valor={hacia} onChange={setHacia} opciones={MONEDAS} />
        </div>

        <div className="order-first space-y-3 lg:order-last lg:col-span-2">
          <Resultado
            label={`${monto.toLocaleString("es-MX")} ${desde} equivalen a`}
            valor={fmt(resultado, hacia)}
            nota={`1 ${desde} = ${tasaUnitaria.toLocaleString("es-MX", { maximumFractionDigits: 4 })} ${hacia}`}
          />
          <div className="tarjeta">
            <Dato label={`1 ${desde}`} valor={`${tasaUnitaria.toLocaleString("es-MX", { maximumFractionDigits: 4 })} ${hacia}`} />
            <Dato
              label={`1 ${hacia}`}
              valor={`${(tasaUnitaria ? 1 / tasaUnitaria : 0).toLocaleString("es-MX", { maximumFractionDigits: 4 })} ${desde}`}
            />
          </div>
          <p className="text-2xs texto-3">
            Son tipos de cambio de referencia del mercado. Tu banco o casa de cambio aplicará un
            diferencial, así que el monto que recibas será algo menor.
          </p>
        </div>
      </Rejilla>
    </div>
  );
}
